/**
 * `useShippingOrigin` — single hook producing `ShippingOriginBinding` for
 * shipping-options UI blocks. One fetch path; one resolution rule (pinned →
 * main fallback). Consolidates two ad-hoc resolvers (the create-flow
 * `shipping-step.tsx` fetched `/api/address-book` and derived `baseCountry`
 * inline; the edit dialog did neither, which is why the currency-mismatch
 * warning never appeared on edit).
 *
 * Returns `{ binding: null }` while loading OR after a transient fetch
 * failure. The consumer renders a skeleton / withholds the shipping UI
 * during this window. Once at least one address is available, the binding
 * is `editable` (mainstream seller surface). The `fixed` kind is for
 * surfaces that bypass the address book (chat-private hard-coded
 * shipping) — they construct the binding directly, not via this hook.
 *
 * Resolution rule:
 *   - `shipFromAddressId` is provided AND matches a fetched row → that row
 *   - else seller's `is_main` row
 *   - else `null` (no addresses on file — invariant violation for any
 *     listing the seller could have created; rendered as "still loading"
 *     since there is no meaningful UI to show, and the picker itself
 *     surfaces the "add a main address" prompt downstream).
 *
 * @see lib/shipping/origin-binding.ts — type SoT
 * @see lib/address-book/list-addresses.ts — boundary-parsed fetch
 */

"use client";

import { useEffect, useMemo, useState } from "react";
import { listAddresses } from "@/lib/address-book/list-addresses";
import type { AddressBookListResponse } from "@kraahl/contract";
import type { ShippingOriginBinding } from "@/lib/shipping/origin-binding";

export interface UseShippingOriginArgs {
  /** Current per-listing pin (`null` ≡ "use main"). */
  shipFromAddressId: string | null;
  /** Persist a new pin. Wired straight into the editable binding. */
  onShipFromAddressChange: (next: string | null) => void;
}

export interface UseShippingOriginResult {
  /** `null` while loading or after a fetch failure. */
  binding: ShippingOriginBinding | null;
}

export function useShippingOrigin({
  shipFromAddressId,
  onShipFromAddressChange,
}: UseShippingOriginArgs): UseShippingOriginResult {
  const [addresses, setAddresses] = useState<AddressBookListResponse | null>(
    null,
  );

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();
    void (async () => {
      try {
        const rows = await listAddresses({ signal: controller.signal });
        if (!cancelled) setAddresses(rows);
      } catch {
        // Silent — consumer keeps `null`, no warning is rendered. Mirrors
        // the prior shipping-step.tsx behaviour: a transient address-book
        // outage shouldn't block the shipping tab; it just degrades to
        // "no origin tier / no warning" until the next refresh.
      }
    })();
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, []);

  const binding = useMemo<ShippingOriginBinding | null>(() => {
    if (!addresses) return null;
    const pinned = shipFromAddressId
      ? addresses.find((a) => a.id === shipFromAddressId) ?? null
      : null;
    const main = addresses.find((a) => a.is_main) ?? null;
    const chosen = pinned ?? main;
    if (!chosen) return null;
    return {
      kind: "editable",
      country: chosen.country,
      address: {
        value: shipFromAddressId,
        onChange: onShipFromAddressChange,
      },
    };
  }, [addresses, shipFromAddressId, onShipFromAddressChange]);

  return { binding };
}
